import type { Transaction } from "@rocicorp/zero";
import { zql } from "./generatedDrizzleSchema";
import { stepDirFromPath } from "./stepConfig";

type FileReference = { path: string; blobHash: string; tenantId: string };
type FileWithContent = FileReference & {
  blob?: { hash: string; tenantId: string; content: string };
};

export function flattenCommitFiles(commit: {
  id: string;
  files: readonly FileWithContent[];
  steps: readonly { stepDir: string; files: readonly FileWithContent[] }[];
}) {
  const fileRow = (file: FileWithContent, path: string) => ({
    commitId: commit.id,
    path,
    blobHash: file.blobHash,
    tenantId: file.tenantId,
    blob: file.blob,
  });
  const sharedSteps = new Set(commit.steps.map((step) => step.stepDir));
  return [
    ...commit.files
      .filter((file) => !sharedSteps.has(stepDirFromPath(file.path) ?? ""))
      .map((file) => fileRow(file, file.path)),
    ...commit.steps.flatMap((step) =>
      step.files.map((file) =>
        fileRow(file, step.stepDir ? `${step.stepDir}/${file.path}` : file.path)
      )
    ),
  ];
}

export async function loadCommitFileRows(
  tx: Pick<Transaction, "run">,
  commitId: string
) {
  const commit = await tx.run(
    zql.workflowCommits
      .where("id", commitId)
      .related("files")
      .related("steps", (step) => step.related("files"))
      .one()
  );
  return commit ? flattenCommitFiles(commit) : [];
}

export async function loadCommitFileRow(
  tx: Pick<Transaction, "run">,
  commitId: string,
  path: string
) {
  const stepDir = stepDirFromPath(path) ?? "";
  const relativePath = stepDir ? path.slice(stepDir.length + 1) : path;
  const commit = await tx.run(
    zql.workflowCommits
      .where("id", commitId)
      .related("files", (file) => file.where("path", path))
      .related("steps", (step) =>
        step
          .where("stepDir", stepDir)
          .related("files", (file) => file.where("path", relativePath))
      )
      .one()
  );
  return commit ? flattenCommitFiles(commit)[0] : undefined;
}

export async function loadManyCommitFileRows(
  tx: Pick<Transaction, "run">,
  commitIds: string[]
) {
  if (commitIds.length === 0) return [];
  const commits = await tx.run(
    zql.workflowCommits
      .where("id", "IN", commitIds)
      .related("files")
      .related("steps", (step) => step.related("files"))
  );
  return commits.flatMap((commit) => flattenCommitFiles(commit));
}

export async function loadCommitFileContents(
  tx: Pick<Transaction, "run">,
  commitId: string
) {
  const commit = await tx.run(
    zql.workflowCommits
      .where("id", commitId)
      .related("files", (file) => file.related("blob"))
      .related("steps", (step) =>
        step.related("files", (file) => file.related("blob"))
      )
      .one()
  );
  return commit ? flattenCommitFiles(commit) : [];
}
